import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface CollapsibleSectionProps {
  title: string;
  count?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  count,
  defaultExpanded = false,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Get the current scroll height for animation targets
  const getScrollHeight = () => contentRef.current?.scrollHeight || 0;

  // When expanded and not animating, use 'none' so content can grow freely.
  // During animation or when collapsed, use a pixel value.
  const getMaxHeight = () => {
    if (isExpanded && !isAnimating) return 'none';
    if (isExpanded && isAnimating) return getScrollHeight();
    return 0;
  };

  const handleTransitionEnd = useCallback(() => {
    setIsAnimating(false);
  }, []);

  const handleToggle = () => {
    if (isExpanded) {
      // Collapsing: set explicit height first so transition works from a value
      setIsAnimating(true);
      // Force a reflow so the browser sees the explicit height before we set 0
      if (contentRef.current) {
        contentRef.current.parentElement!.style.maxHeight = `${getScrollHeight()}px`;
        // eslint-disable-next-line no-unused-expressions
        contentRef.current.parentElement!.offsetHeight; // force reflow
      }
    } else {
      // Expanding: animate from 0 to scrollHeight, then switch to 'none'
      setIsAnimating(true);
    }
    setIsExpanded(!isExpanded);
  };

  // On first render when defaultExpanded, no animation needed
  useEffect(() => {
    if (isExpanded && !isAnimating && contentRef.current) {
      contentRef.current.parentElement!.style.maxHeight = 'none';
    }
  }, [children, isExpanded, isAnimating]);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center justify-between w-full py-2"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            {title}
          </h3>
          {count !== undefined && count > 0 && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5"
              style={{ background: '#E9E3F0', color: '#744DA1' }}
            >
              {count}
            </span>
          )}
        </div>
        <ChevronDownIcon
          className="h-4 w-4 text-gray-500 transition-transform duration-300"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
      <div
        onTransitionEnd={handleTransitionEnd}
        style={{
          maxHeight: getMaxHeight(),
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out',
        }}
      >
        <div ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
